import {
  Box,
  List,
  ListItemText,
  ListItemButton,
  ListSubheader,
} from '@mui/material';
import { format, isToday, isYesterday, isValid } from 'date-fns';
import { useMemo } from 'react';
import {
  IChatbotFlowModel,
  IChatbotSessionModel,
} from '../../../data/models/chatbot';

interface HistoryProps {
  chatbot: IChatbotFlowModel;
  currentSessionId: string;
  onChangeSession: (sessionId: string) => void;
}

export function History({
  chatbot,
  currentSessionId,
  onChangeSession,
}: Readonly<HistoryProps>) {
  const groupedChatsByDate = useMemo(() => {
    if (!chatbot.sessions || chatbot.sessions.length === 0) return {};

    const filteredChats = chatbot.sessions.filter(
      chat => chat.id !== 'default',
    );

    const grouped = filteredChats.reduce<
      Record<string, IChatbotSessionModel[]>
    >((acc, chat) => {
      if (chat.entries.length === 0) {
        const dateKey = 'Today';
        acc[dateKey] = [...(acc[dateKey] || []), chat];
        return acc;
      }

      // Get the timestamp of the last entry
      const lastEntryTimestamp =
        chat.entries[chat.entries.length - 1]?.timestamp;

      if (!isValid(Date.parse(lastEntryTimestamp))) {
        const dateKey = 'Today';
        acc[dateKey] = [...(acc[dateKey] || []), chat];
        return acc;
      }

      let dateKey: string;
      const parsedDate = Date.parse(lastEntryTimestamp);

      if (isToday(parsedDate)) {
        dateKey = 'Today';
        acc[dateKey] = [...(acc[dateKey] || []), chat];
      } else if (isYesterday(parsedDate)) {
        dateKey = 'Yesterday';
        acc[dateKey] = [...(acc[dateKey] || []), chat];
      } else {
        dateKey = format(parsedDate, 'MM/dd/yyyy');
        acc[dateKey] = [...(acc[dateKey] || []), chat];
      }

      return acc;
    }, {});

    // Sort the entries by date in descending order
    const sortedGroups = Object.fromEntries(
      Object.entries(grouped).sort(([dateA], [dateB]) => {
        if (dateA === 'Today') return -1;
        if (dateB === 'Today') return 1;
        if (dateA === 'Yesterday') return -1;
        if (dateB === 'Yesterday') return 1;

        const [monthA, dayA, yearA] = dateA.split('/').map(Number);
        const [monthB, dayB, yearB] = dateB.split('/').map(Number);

        const dateAObj = new Date(yearA, monthA - 1, dayA);
        const dateBObj = new Date(yearB, monthB - 1, dayB);

        return dateBObj.getTime() - dateAObj.getTime();
      }),
    );

    // Sort chats within each group by their last entry timestamp
    Object.keys(sortedGroups).forEach(dateKey => {
      sortedGroups[dateKey].sort((chatA, chatB) => {
        const timestampA =
          chatA.entries[chatA.entries.length - 1]?.timestamp || '';
        const timestampB =
          chatB.entries[chatB.entries.length - 1]?.timestamp || '';

        if (!timestampA) return 1;
        if (!timestampB) return -1;

        return Date.parse(timestampB) - Date.parse(timestampA);
      });
    });

    return sortedGroups;
  }, [chatbot.sessions]);

  return (
    <Box
      sx={{
        maxHeight: '300px',
        overflow: 'auto',
      }}
    >
      <List subheader={<ListSubheader component="div">Primary</ListSubheader>}>
        <ListItemButton
          key="default"
          onClick={() => onChangeSession('default')}
        >
          <ListItemText
            primary="Primary"
            sx={{
              color:
                'default' === currentSessionId
                  ? 'primary.main'
                  : 'text.primary',
            }}
          />
        </ListItemButton>
      </List>
      {Object.entries(groupedChatsByDate).map(([date, chats]) => (
        <List
          key={date}
          subheader={<ListSubheader component="div">{date}</ListSubheader>}
        >
          {chats.map(chat => (
            <ListItemButton
              key={chat.id}
              onClick={() => onChangeSession(chat.id)}
            >
              <ListItemText
                primary={chat.name}
                secondary={
                  chat.entries.length > 0 &&
                  format(
                    Date.parse(chat.entries[chat.entries.length - 1].timestamp),
                    'HH:mm',
                  )
                }
                sx={{
                  color:
                    chat.id === currentSessionId
                      ? 'primary.main'
                      : 'text.primary',
                }}
              />
            </ListItemButton>
          ))}
        </List>
      ))}
    </Box>
  );
}
